import {
  StandardMemberAttributesDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import {
  CollapsibleCard,
  FlexContainer,
  getNormalizedGender,
  IconLibrary,
} from '@act/storybook';
import { Box, Typography } from '@mui/material';
import usePersonalInformationDialog from 'react-components/dialog-forms/hooks/use-personal-information-dialog';
import { Fragment } from 'react/jsx-runtime';
import { LabeledDatum } from 'toolkit/styles/dataDisplay';
import { TaggableChip } from '@act/storybook';
import { timeFormatter } from '@act/shared/utilities';
import { formatRaceEthnicitySummaryForDisplay } from '../../dialog-forms/helpers/personal-information-formatters';
import { getClientFeedDataForSection } from './getClientFeedData';
import { clientAppSelectors } from 'redux/slices/clientSlice';
import { formatPhoneNumber } from '@act/shared/utilities';
import { checkBirthday } from 'react-components/docket/components/utilities/displayNameAndBirthdayCheck.utils';

export interface PersonalInformationCardProps {
  member: UserDto;
  attributes: Pick<StandardMemberAttributesDto, 'clientFeedData'>;
  defaultClosed?: boolean;
  onToggle?: () => void;
}

const PersonalInformationCard = ({
  member,
  attributes,
  defaultClosed = false,
  onToggle,
}: PersonalInformationCardProps) => {
  const openPersonalInformationDialog = usePersonalInformationDialog();

  const clientFeedData = getClientFeedDataForSection(
    attributes?.clientFeedData,
    'personal_information'
  );
  const client = clientAppSelectors.useSelectClientById(member.clientId);

  return (
    <CollapsibleCard
      title="Personal Information"
      actions={[
        {
          display: 'Edit personal information',
          callback: () => openPersonalInformationDialog({ member }),
        },
      ]}
      avatar={<IconLibrary iconName="User" size={24} />}
      twoColumn
      collapsible
      defaultClosed={defaultClosed}
      onToggle={onToggle}
    >
      <LabeledDatum label="Full name" datum={member.name ?? 'Unknown'} />
      <LabeledDatum
        label="Preferred name"
        datum={member.preferredName || '-'}
      />
      <LabeledDatum
        label="Name pronunciation"
        datum={member.namePronunciation || '-'}
      />
      <LabeledDatum
        label="Date of birth"
        datum={
          member.dateOfBirth
            ? timeFormatter.formatDateOfBirthString(member.dateOfBirth) +
              ' ' +
              checkBirthday(member.dateOfBirth)
            : 'Unknown'
        }
      />
      <LabeledDatum label="Gender" datum={getNormalizedGender(member)} />
      <LabeledDatum label="Pronouns" datum={member.pronouns || '-'} />
      <LabeledDatum
        label="Race / ethnicity"
        datum={
          member.raceEthnicitySummary
            ? formatRaceEthnicitySummaryForDisplay(member.raceEthnicitySummary)
            : 'Unknown'
        }
      />
      <LabeledDatum
        label="Preferred language"
        datum={member.reportedLanguage ?? 'Unknown'}
      />
      {!!member?.memberSegments?.length && (
        <LabeledDatum
          label="Segments"
          datum={
            <FlexContainer direction="column" gap={1} margin="8px 0 0 0">
              {member.memberSegments.map(memberSegment => (
                <TaggableChip
                  key={`segment-chip-${memberSegment?.segment?.name}`}
                  variant="filled"
                  label={memberSegment.segment?.name}
                />
              ))}
            </FlexContainer>
          }
        />
      )}
      {!!client?.clientMarketNumbers?.length && (
        <LabeledDatum
          label="Reema market numbers"
          datum={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {client.clientMarketNumbers.map(marketNumber => (
                <Typography
                  key={`marketNumber-${marketNumber.id}`}
                  variant="bodyLarge"
                >
                  {`${marketNumber.name} · ${formatPhoneNumber(
                    marketNumber.number
                  )}`}
                </Typography>
              ))}
            </Box>
          }
        />
      )}
      {clientFeedData?.length > 0 && (
        <Fragment>
          {clientFeedData.map(data => {
            return (
              <LabeledDatum
                key={data.field_label}
                label={data.field_label}
                datum={data.field_value}
              />
            );
          })}
        </Fragment>
      )}
    </CollapsibleCard>
  );
};

export default PersonalInformationCard;
